/** 触发设置 — 按键选择与触发点调节 */
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dropdown,
  Iconfont,
  Slider,
  Switch,
  Tooltip,
} from "@eweadn-qiankun/shared-components";
import { mouseIsAlpha6 } from "../../domain/mouseIdentity";

export const TRIGGER_POINT_DEVICE_MAX = 9;
/** UI 触发点 1~5 档，底层协议映射 0~9 */
export const TRIGGER_POINT_UI_MIN = 1;
export const TRIGGER_POINT_UI_MAX = 5;
export const TRIGGER_POINT_DEFAULT = 3;
export const PRESS_DEPTH_MAX = 10;
/** 1~5 档中间刻度线数量（2、3、4 档） */
export const TRIGGER_POINT_SCALE_COUNT =
  TRIGGER_POINT_UI_MAX - TRIGGER_POINT_UI_MIN - 1;

export const clampTriggerPointUi = (ui: number) =>
  Math.min(
    TRIGGER_POINT_UI_MAX,
    Math.max(TRIGGER_POINT_UI_MIN, Number(ui) || TRIGGER_POINT_UI_MIN)
  );
/** 与触发点 1~5 档对齐：首尾 + 中间 3 根，共 5 根刻度 */
export const PRESS_DEPTH_SCALE_COUNT = TRIGGER_POINT_SCALE_COUNT;

/** Alpha6：UI 1~10 档与 SDK 0~9 一一对应（无缩放取整） */
export const ALPHA6_TRIGGER_UI_MIN = 1;
export const ALPHA6_TRIGGER_UI_MAX = 10;
export const ALPHA6_TRIGGER_DEVICE_MIN = 0;
export const ALPHA6_TRIGGER_DEVICE_MAX = 9;
export const ALPHA6_TRIGGER_DEFAULT = 5;
export const ALPHA6_TRIGGER_SCALE_COUNT =
  ALPHA6_TRIGGER_UI_MAX - ALPHA6_TRIGGER_UI_MIN - 1;

export type TriggerPointProfile = "legacy" | "alpha6";

export const getTriggerPointProfile = (
  deviceId?: string
): TriggerPointProfile => (mouseIsAlpha6(deviceId) ? "alpha6" : "legacy");

export const getTriggerPointUiConfig = (profile: TriggerPointProfile) =>
  profile === "alpha6"
    ? {
        min: ALPHA6_TRIGGER_UI_MIN,
        max: ALPHA6_TRIGGER_UI_MAX,
        scaleCount: ALPHA6_TRIGGER_SCALE_COUNT,
        default: ALPHA6_TRIGGER_DEFAULT,
      }
    : {
        min: TRIGGER_POINT_UI_MIN,
        max: TRIGGER_POINT_UI_MAX,
        scaleCount: TRIGGER_POINT_SCALE_COUNT,
        default: TRIGGER_POINT_DEFAULT,
      };

export const clampAlpha6TriggerUi = (ui: number) =>
  Math.min(
    ALPHA6_TRIGGER_UI_MAX,
    Math.max(ALPHA6_TRIGGER_UI_MIN, Number(ui) || ALPHA6_TRIGGER_UI_MIN)
  );

export const clampTriggerPointUiForProfile = (
  ui: number,
  profile: TriggerPointProfile
) =>
  profile === "alpha6" ? clampAlpha6TriggerUi(ui) : clampTriggerPointUi(ui);

/** 均匀刻度位置（%）；includeEnds 时在 0%、100% 各加一根 */
export const buildScaleTickPercents = (
  middleCount: number,
  includeEnds: boolean
): number[] => {
  const segments = middleCount + 1;
  if (includeEnds) {
    return Array.from(
      { length: middleCount + 2 },
      (_, i) => (i / segments) * 100
    );
  }
  return Array.from(
    { length: middleCount },
    (_, i) => ((i + 1) / segments) * 100
  );
};

export const deviceTriggerToUi = (device: number) => {
  const clamped = Math.min(
    TRIGGER_POINT_DEVICE_MAX,
    Math.max(0, Number(device) || 0)
  );
  return (
    Math.round(
      (clamped / TRIGGER_POINT_DEVICE_MAX) *
        (TRIGGER_POINT_UI_MAX - TRIGGER_POINT_UI_MIN)
    ) + TRIGGER_POINT_UI_MIN
  );
};

export const uiTriggerToDevice = (ui: number) => {
  const clamped = clampTriggerPointUi(ui);
  return Math.round(
    ((clamped - TRIGGER_POINT_UI_MIN) /
      (TRIGGER_POINT_UI_MAX - TRIGGER_POINT_UI_MIN)) *
      TRIGGER_POINT_DEVICE_MAX
  );
};

export const deviceTriggerToUiAlpha6 = (device: number) => {
  const clamped = Math.min(
    ALPHA6_TRIGGER_DEVICE_MAX,
    Math.max(
      ALPHA6_TRIGGER_DEVICE_MIN,
      Number(device) || ALPHA6_TRIGGER_DEVICE_MIN
    )
  );
  return clamped + 1;
};

export const uiTriggerToDeviceAlpha6 = (ui: number) =>
  clampAlpha6TriggerUi(ui) - 1;

export const deviceTriggerToUiForProfile = (
  device: number,
  profile: TriggerPointProfile
) =>
  profile === "alpha6"
    ? deviceTriggerToUiAlpha6(device)
    : deviceTriggerToUi(device);

export const uiTriggerToDeviceForProfile = (
  ui: number,
  profile: TriggerPointProfile
) =>
  profile === "alpha6" ? uiTriggerToDeviceAlpha6(ui) : uiTriggerToDevice(ui);

/**
 * 快速触发 power（匠盟 HIDHandle）：0=关，1~5 档对应 -25/-50/-75/-100/-125（每档 25）
 * UI 滑块 1~5 与协议档位一一对应，不做 0~127 线性映射。
 */
export const FAST_TRIGGER_POWER_UI_MIN = 1;
export const FAST_TRIGGER_POWER_UI_MAX = 5;
/** 快速触发「恢复默认」档位（协议 power=2，对应 -50） */
export const FAST_TRIGGER_POWER_DEFAULT = 2;
/** @deprecated 使用 FAST_TRIGGER_POWER_UI_MIN */
export const MAGNETIC_FEATURE_UI_MIN = FAST_TRIGGER_POWER_UI_MIN;
/** @deprecated 使用 FAST_TRIGGER_POWER_UI_MAX */
export const MAGNETIC_FEATURE_UI_MAX = FAST_TRIGGER_POWER_UI_MAX;
/** @deprecated 使用 FAST_TRIGGER_POWER_DEFAULT */
export const MAGNETIC_FEATURE_UI_DEFAULT = FAST_TRIGGER_POWER_DEFAULT;
/** 触觉反馈协议档位（0=关闭，1~5=强度） */
export const TACTILE_FEEDBACK_DEVICE_MAX = 5;

/** 触觉反馈滑块 1~5 档（0 仅由开关控制，不在滑块上展示） */
export const TACTILE_FEEDBACK_SLIDER_MIN = 1;
export const TACTILE_FEEDBACK_SLIDER_MAX = 5;
export const TACTILE_FEEDBACK_UI_DEFAULT = 3;
export const TACTILE_FEEDBACK_SCALE_COUNT =
  TACTILE_FEEDBACK_SLIDER_MAX - TACTILE_FEEDBACK_SLIDER_MIN - 1;

export const clampTactileFeedbackSliderUi = (ui: number) =>
  Math.min(
    TACTILE_FEEDBACK_SLIDER_MAX,
    Math.max(
      TACTILE_FEEDBACK_SLIDER_MIN,
      Number.isFinite(Number(ui)) ? Number(ui) : TACTILE_FEEDBACK_SLIDER_MIN
    )
  );

/** 内部状态：0=关闭，1~5=强度 */
export const clampTactileFeedbackUi = (ui: number) => {
  const n = Number(ui);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return clampTactileFeedbackSliderUi(n);
};

export const clampFastTriggerPowerUi = (ui: number) =>
  Math.min(
    FAST_TRIGGER_POWER_UI_MAX,
    Math.max(
      FAST_TRIGGER_POWER_UI_MIN,
      Number.isFinite(Number(ui)) ? Number(ui) : FAST_TRIGGER_POWER_UI_MIN
    )
  );

/** 协议 power 0~5 → UI 1~5；power=0 且开关开启时按最低档 1 展示 */
export const deviceFastTriggerPowerToUi = (
  device: number,
  _enabled = true
): number => {
  const n = Math.floor(Number(device) || 0);
  if (n <= 0) {
    // 出厂 power=0：按最低有效档展示，与「恢复默认」写入的 2 档区分
    return FAST_TRIGGER_POWER_UI_MIN;
  }
  return Math.min(
    FAST_TRIGGER_POWER_UI_MAX,
    Math.max(FAST_TRIGGER_POWER_UI_MIN, n)
  );
};

/** UI 1~5 → 协议 power 1~5 */
export const uiFastTriggerPowerToDevice = (ui: number): number =>
  clampFastTriggerPowerUi(ui);

/** @deprecated 使用 deviceFastTriggerPowerToUi */
export const deviceMagneticPowerToUi = (
  device: number,
  _deviceMax?: number,
  enabled = true
) => deviceFastTriggerPowerToUi(device, enabled);

/** @deprecated 使用 uiFastTriggerPowerToDevice */
export const uiMagneticPowerToDevice = (ui: number, _deviceMax?: number) =>
  uiFastTriggerPowerToDevice(ui);

/** @deprecated 使用 clampFastTriggerPowerUi */
export const clampMagneticFeatureUi = clampFastTriggerPowerUi;

export const deviceTactileFeedbackToUi = (device: number) =>
  clampTactileFeedbackUi(Number(device) || 0);

export const uiTactileFeedbackToDevice = (ui: number) =>
  clampTactileFeedbackUi(ui);

export type TriggerButtonTarget = "both" | "left" | "right";

export interface TriggerSettingPanelProps {
  disabled?: boolean;
  selectedButton: TriggerButtonTarget;
  onSelectedButtonChange: (target: TriggerButtonTarget) => void;
  triggerPoint: number;
  currentRoute: number;
  onTriggerPointChange: (value: number) => void;
  onTriggerPointComplete: (value: number) => void;
  /** 打开按键校准弹窗 */
  onOpenCalibration?: () => void;
  /** 磁微动扩展：快速触发 / 触觉反馈（Alpha6 等） */
  showMagneticExtras?: boolean;
  showButtonSound?: boolean;
  /** 按压深度档位配置；Alpha6 为 10 档 */
  triggerPointProfile?: TriggerPointProfile;
  /** Alpha6：实时按压深度进度条（与鼠标 SVG 动画联动） */
  showLivePressDepth?: boolean;
  /** 温馨提示文案变体；默认 4 条含校准说明，alpha6 为 3 条且无校准入口 */
  tipsVariant?: "default" | "alpha6";
  fastTriggerEnabled?: boolean;
  fastTriggerPower?: number;
  onFastTriggerEnabledChange?: (enabled: boolean) => void;
  onFastTriggerPowerChange?: (value: number) => void;
  onFastTriggerPowerComplete?: (value: number) => void;
  vibrationEnabled?: boolean;
  vibrationPower?: number;
  onVibrationEnabledChange?: (enabled: boolean) => void;
  onVibrationPowerChange?: (value: number) => void;
  onVibrationPowerComplete?: (value: number) => void;
  buttonSoundEnabled?: boolean;
  onButtonSoundEnabledChange?: (enabled: boolean) => void;
}

const TriggerSettingPanel: React.FC<TriggerSettingPanelProps> = ({
  disabled = false,
  selectedButton,
  onSelectedButtonChange,
  triggerPoint,
  currentRoute,
  onTriggerPointChange,
  onTriggerPointComplete,
  onOpenCalibration,
  showMagneticExtras = false,
  showButtonSound = false,
  triggerPointProfile = "legacy",
  showLivePressDepth = false,
  tipsVariant = "default",
  fastTriggerEnabled = false,
  fastTriggerPower = FAST_TRIGGER_POWER_DEFAULT,
  onFastTriggerEnabledChange,
  onFastTriggerPowerChange,
  onFastTriggerPowerComplete,
  vibrationEnabled = false,
  vibrationPower = TACTILE_FEEDBACK_UI_DEFAULT,
  onVibrationEnabledChange,
  onVibrationPowerChange,
  onVibrationPowerComplete,
  buttonSoundEnabled = false,
  onButtonSoundEnabledChange,
}) => {
  const { t } = useTranslation();
  const triggerPointUi = getTriggerPointUiConfig(triggerPointProfile);

  const buttonOptions = useMemo(
    () => [
      { key: "left", label: t("mouse.trigger.leftButton") },
      { key: "right", label: t("mouse.trigger.rightButton") },
      { key: "both", label: t("mouse.trigger.buttonBoth") },
    ],
    [t]
  );

  const depth = Number.isFinite(currentRoute) ? currentRoute : 0;
  const depthPercent = Math.min(
    100,
    Math.max(0, (depth / PRESS_DEPTH_MAX) * 100)
  );
  const depthTickPercents = useMemo(
    () => buildScaleTickPercents(triggerPointUi.scaleCount, true),
    [triggerPointUi.scaleCount]
  );

  const handleRestoreTriggerPoint = () => {
    onTriggerPointChange(triggerPointUi.default);
    onTriggerPointComplete(triggerPointUi.default);
  };

  const handleRestoreFastTrigger = () => {
    if (!onFastTriggerPowerChange || !onFastTriggerPowerComplete) return;
    onFastTriggerPowerChange(FAST_TRIGGER_POWER_DEFAULT);
    onFastTriggerPowerComplete(FAST_TRIGGER_POWER_DEFAULT);
  };

  const handleRestoreVibration = () => {
    if (!onVibrationPowerChange || !onVibrationPowerComplete) return;
    onVibrationPowerChange(TACTILE_FEEDBACK_UI_DEFAULT);
    onVibrationPowerComplete(TACTILE_FEEDBACK_UI_DEFAULT);
  };

  const vibrationSliderValue =
    vibrationPower > 0 ? vibrationPower : TACTILE_FEEDBACK_UI_DEFAULT;

  const tipKeys =
    tipsVariant === "alpha6"
      ? [
          "mouse.trigger.triggerAlpha6Tip1",
          "mouse.trigger.triggerAlpha6Tip2",
          "mouse.trigger.triggerAlpha6Tip3",
        ]
      : [
          "mouse.trigger.triggerTip1",
          "mouse.trigger.triggerTip2",
          "mouse.trigger.triggerTip3",
          "mouse.trigger.triggerTip4",
        ];

  return (
    <div className="trigger-panel-stack">
      <section className="trigger-card trigger-card--primary">
        <header className="trigger-card-hd">
          <div className="trigger-card-hd-row">
            <h2>{t("mouse.menu.triggerSetting")}</h2>
            {onOpenCalibration ? (
              <Tooltip
                text={t("mouse.trigger.buttonCalibrationTitle")}
                placement="top"
                usePortal
              >
                <button
                  type="button"
                  className="trigger-cal-entry"
                  disabled={disabled}
                  aria-label={t("mouse.trigger.buttonCalibrationTitle")}
                  onClick={onOpenCalibration}
                >
                  <Iconfont icon="compass" />
                </button>
              </Tooltip>
            ) : null}
          </div>
          <p>{t("mouse.trigger.pageDesc")}</p>
        </header>

        <div className="trigger-card-bd">
          <div className="trigger-field trigger-field-select">
            <span className="trigger-label">
              <Iconfont icon="shubiaoshifang" className="trigger-label-icon" />
              {t("mouse.trigger.selectButton")}
            </span>
            <Dropdown
              trigger="click"
              disabled={disabled}
              options={buttonOptions}
              value={selectedButton}
              onSelect={(key) =>
                onSelectedButtonChange(key as TriggerButtonTarget)
              }
              align="left"
            >
              <button type="button" className="trigger-select">
                <span className="trigger-select-text">
                  {buttonOptions.find((o) => o.key === selectedButton)?.label ??
                    t("mouse.trigger.selectButton")}
                </span>
                <Iconfont icon="down" className="trigger-select-arrow" />
              </button>
            </Dropdown>
          </div>

          <div className="trigger-section">
            <div className="trigger-section-hd">
              <span className="trigger-label">
                <Iconfont icon="anyashendu" className="trigger-label-icon" />
                {t("mouse.trigger.pressForce")}
              </span>
            </div>

            <div className="trigger-point-row">
              <Slider
                className="trigger-slider"
                min={triggerPointUi.min}
                max={triggerPointUi.max}
                step={1}
                scaleCount={triggerPointUi.scaleCount}
                value={triggerPoint}
                disabled={disabled}
                showValue
                unit=""
                onChange={(v: number) =>
                  onTriggerPointChange(
                    clampTriggerPointUiForProfile(v, triggerPointProfile)
                  )
                }
                onChangeComplete={(v: number) =>
                  onTriggerPointComplete(
                    clampTriggerPointUiForProfile(v, triggerPointProfile)
                  )
                }
              />
              <button
                type="button"
                className="trigger-reset"
                disabled={disabled}
                title={t("mouse.trigger.restoreDefault")}
                aria-label={t("mouse.trigger.restoreDefault")}
                onClick={handleRestoreTriggerPoint}
              >
                <Iconfont icon="reset" />
              </button>
            </div>

            {showLivePressDepth ? (
              <>
                <div className="trigger-depth-row">
                  <div
                    className={[
                      "trigger-depth",
                      depthPercent > 0 ? "trigger-depth--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-label={t("mouse.trigger.livePressDepth")}
                  >
                    <div
                      className="trigger-depth-fill"
                      style={{ width: `${depthPercent}%` }}
                    />
                    <div className="trigger-depth-ticks" aria-hidden>
                      {depthTickPercents.map((left, i) => (
                        <span
                          key={i}
                          className={[
                            "trigger-depth-tick",
                            i === 0 ? "trigger-depth-tick-start" : "",
                            i === depthTickPercents.length - 1
                              ? "trigger-depth-tick-end"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          style={{ left: `${left}%` }}
                        />
                      ))}
                    </div>
                  </div>
                  <span className="trigger-slider-row-suffix" aria-hidden />
                </div>

                <div className="trigger-point-scale-labels">
                  <div className="trigger-point-scale-labels-inner">
                    <span>{t("mouse.trigger.pressDepthLight")}</span>
                    <span>{t("mouse.trigger.pressDepthHeavy")}</span>
                  </div>
                  <span className="trigger-slider-row-suffix" aria-hidden />
                </div>
              </>
            ) : null}
          </div>

          {showMagneticExtras ? (
            <>
              <div className="trigger-section">
                <div className="trigger-section-hd trigger-section-hd--switch">
                  <span className="trigger-label">
                    <Iconfont
                      icon="kuaisuchufa"
                      className="trigger-label-icon"
                    />
                    {t("mouse.trigger.fastTriggerTitle")}
                  </span>
                  <Switch
                    checked={fastTriggerEnabled}
                    disabled={disabled}
                    onChange={(checked) =>
                      onFastTriggerEnabledChange?.(checked)
                    }
                  />
                </div>
                <div className="trigger-point-row trigger-point-row--compact">
                  <Slider
                    className="trigger-slider"
                    min={FAST_TRIGGER_POWER_UI_MIN}
                    max={FAST_TRIGGER_POWER_UI_MAX}
                    step={1}
                    scaleCount={TRIGGER_POINT_SCALE_COUNT}
                    value={fastTriggerPower}
                    disabled={disabled || !fastTriggerEnabled}
                    showValue
                    unit=""
                    onChange={(v: number) =>
                      onFastTriggerPowerChange?.(clampFastTriggerPowerUi(v))
                    }
                    onChangeComplete={(v: number) =>
                      onFastTriggerPowerComplete?.(clampFastTriggerPowerUi(v))
                    }
                  />
                  <button
                    type="button"
                    className="trigger-reset"
                    disabled={disabled || !fastTriggerEnabled}
                    title={t("mouse.trigger.restoreDefault")}
                    aria-label={t("mouse.trigger.restoreDefault")}
                    onClick={handleRestoreFastTrigger}
                  >
                    <Iconfont icon="reset" />
                  </button>
                </div>
              </div>

              <div className="trigger-section">
                <div className="trigger-section-hd trigger-section-hd--switch">
                  <span className="trigger-label">
                    <Iconfont
                      icon="chujuefankui"
                      className="trigger-label-icon"
                    />
                    {t("mouse.trigger.vibrationIntensity")}
                  </span>
                  <Switch
                    checked={vibrationEnabled}
                    disabled={disabled}
                    onChange={(checked) => onVibrationEnabledChange?.(checked)}
                  />
                </div>
                <div className="trigger-point-row trigger-point-row--compact">
                  <Slider
                    className="trigger-slider"
                    min={TACTILE_FEEDBACK_SLIDER_MIN}
                    max={TACTILE_FEEDBACK_SLIDER_MAX}
                    step={1}
                    scaleCount={TACTILE_FEEDBACK_SCALE_COUNT}
                    value={vibrationSliderValue}
                    disabled={disabled || !vibrationEnabled}
                    showValue
                    unit=""
                    onChange={(v: number) =>
                      onVibrationPowerChange?.(clampTactileFeedbackSliderUi(v))
                    }
                    onChangeComplete={(v: number) =>
                      onVibrationPowerComplete?.(
                        clampTactileFeedbackSliderUi(v)
                      )
                    }
                  />
                  <button
                    type="button"
                    className="trigger-reset"
                    disabled={disabled || !vibrationEnabled}
                    title={t("mouse.trigger.restoreDefault")}
                    aria-label={t("mouse.trigger.restoreDefault")}
                    onClick={handleRestoreVibration}
                  >
                    <Iconfont icon="reset" />
                  </button>
                </div>
                <div className="trigger-point-scale-labels">
                  <div className="trigger-point-scale-labels-inner">
                    <span>{t("mouse.trigger.vibrationWeak")}</span>
                    <span>{t("mouse.trigger.vibrationStrong")}</span>
                  </div>
                  <span className="trigger-slider-row-suffix" aria-hidden />
                </div>
              </div>
            </>
          ) : null}

          <aside className="trigger-tips trigger-tips--in-card">
            <h3>{t("mouse.trigger.tipsTitle")}</h3>
            <ol>
              {tipKeys.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ol>
          </aside>
        </div>
      </section>

      {showButtonSound ? (
        <section className="trigger-card trigger-card--feature trigger-card--feature-sound">
          <div className="trigger-card-hd trigger-field-hd-row">
            <h2 className="trigger-card-title-sm">
              {t("mouse.trigger.buttonSoundTitle")}
            </h2>
            <Switch
              checked={buttonSoundEnabled}
              disabled={disabled}
              onChange={(checked) => onButtonSoundEnabledChange?.(checked)}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default TriggerSettingPanel;
